import { useMemo, useState } from "react";
import {
  PROMOTION_KIND_LABELS,
  type PromotionDto,
  type PromotionInput,
} from "@school-sales-ai/shared";
import { formatMxn } from "@/lib/format-mxn";
import {
  emptyPromotionForm,
  previewPriceCents,
  promotionToForm,
  toPromotionInput,
  WEEKDAY_LABELS,
  willAnnounce,
  type PromotionFormValues,
} from "./promotion-form";

type CatalogProduct = { id: string; name: string; priceCents: number | null };

type Props = {
  promotion: PromotionDto | null;
  products: CatalogProduct[];
  currency: string;
  saving: boolean;
  onCancel: () => void;
  onSave: (input: PromotionInput) => void;
};

export function PromotionEditorDialog({
  promotion,
  products,
  currency,
  saving,
  onCancel,
  onSave,
}: Props): React.JSX.Element {
  const [values, setValues] = useState<PromotionFormValues>(() =>
    promotion ? promotionToForm(promotion) : emptyPromotionForm(),
  );
  // Errors appear on submit, not while typing: a half-written amount is not a
  // mistake yet.
  const [errors, setErrors] = useState<Map<string, string>>(new Map());

  const set = <K extends keyof PromotionFormValues>(key: K, value: PromotionFormValues[K]): void =>
    setValues((v) => ({ ...v, [key]: value }));

  const toggleWeekday = (day: number): void =>
    setValues((v) => ({
      ...v,
      weekdays: v.weekdays.includes(day)
        ? v.weekdays.filter((d) => d !== day)
        : [...v.weekdays, day],
    }));

  const toggleProduct = (id: string): void =>
    setValues((v) => ({
      ...v,
      productIds: v.productIds.includes(id)
        ? v.productIds.filter((p) => p !== id)
        : [...v.productIds, id],
    }));

  const chosen = useMemo(
    () => products.filter((p) => values.productIds.includes(p.id)),
    [products, values.productIds],
  );

  const submit = (): void => {
    const result = toPromotionInput(values);
    if (!result.ok) {
      setErrors(result.errors);
      return;
    }
    setErrors(new Map());
    onSave(result.input);
  };

  const err = (key: string): React.JSX.Element | null => {
    const message = errors.get(key);
    return message ? <p className="mt-1 text-xs text-red-600">{message}</p> : null;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4">
      <div className="my-8 w-full max-w-2xl rounded-lg bg-white p-6 shadow-xl">
        <h2 className="text-lg font-semibold text-ink-900">
          {promotion ? "Editar promoción" : "Nueva promoción"}
        </h2>

        <label className="mt-4 block text-sm font-medium text-ink-800">
          Nombre
          <input
            type="text"
            value={values.name}
            onChange={(e) => set("name", e.target.value)}
            maxLength={120}
            placeholder="Happy hour de tequila"
            className="mt-1 w-full rounded-md border border-ink-200 px-3 py-2 text-sm"
          />
        </label>
        <p className="mt-1 text-xs text-ink-500">
          Si eliges que el agente la mencione, éste es el nombre que le dirá al cliente.
        </p>
        {err("name")}

        {/* ── What it does to the price ── */}
        <fieldset className="mt-5">
          <legend className="text-sm font-medium text-ink-800">Qué hace con el precio</legend>
          <div className="mt-2 flex flex-wrap gap-4">
            {(["percent_off", "price_override"] as const).map((kind) => (
              <label key={kind} className="flex items-center gap-2 text-sm text-ink-700">
                <input
                  type="radio"
                  name="promotion-kind"
                  checked={values.kind === kind}
                  onChange={() => set("kind", kind)}
                />
                {PROMOTION_KIND_LABELS[kind]}
              </label>
            ))}
          </div>

          {values.kind === "percent_off" ? (
            <label className="mt-3 block text-sm text-ink-800">
              Descuento
              <div className="mt-1 flex items-center gap-2">
                <input
                  type="text"
                  inputMode="decimal"
                  value={values.percent}
                  onChange={(e) => set("percent", e.target.value)}
                  placeholder="15"
                  className="w-28 rounded-md border border-ink-200 px-3 py-2 text-sm"
                />
                <span className="text-sm text-ink-600">%</span>
              </div>
              {err("percentOffBp")}
            </label>
          ) : (
            <label className="mt-3 block text-sm text-ink-800">
              Precio durante la promoción
              <div className="mt-1 flex items-center gap-2">
                <span className="text-sm text-ink-600">$</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={values.price}
                  onChange={(e) => set("price", e.target.value)}
                  placeholder="199.00"
                  className="w-36 rounded-md border border-ink-200 px-3 py-2 text-sm"
                />
                <span className="text-sm text-ink-600">{currency}</span>
              </div>
              <p className="mt-1 text-xs text-ink-500">
                Puede ser mayor que el precio de lista — sirve para manejar precios
                distintos según la hora del día.
              </p>
              {err("overridePriceCents")}
            </label>
          )}
        </fieldset>

        {/* ── Products ── */}
        <fieldset className="mt-5">
          <legend className="text-sm font-medium text-ink-800">Productos</legend>
          <div className="mt-2 max-h-48 overflow-y-auto rounded-md border border-ink-200 p-2">
            {products.length === 0 ? (
              <p className="p-2 text-sm text-ink-500">
                No hay productos en el catálogo todavía.
              </p>
            ) : (
              products.map((p) => (
                <label
                  key={p.id}
                  className="flex items-center justify-between gap-3 rounded px-2 py-1.5 text-sm hover:bg-ink-50"
                >
                  <span className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={values.productIds.includes(p.id)}
                      onChange={() => toggleProduct(p.id)}
                    />
                    {p.name}
                  </span>
                  <span className="text-xs text-ink-500">
                    {formatMxn(p.priceCents, currency)}
                  </span>
                </label>
              ))
            )}
          </div>
          {err("productIds")}
        </fieldset>

        {/* ── When ── */}
        <fieldset className="mt-5">
          <legend className="text-sm font-medium text-ink-800">Cuándo aplica</legend>
          <p className="mt-1 text-xs text-ink-500">
            Deja en blanco lo que no quieras limitar. Sin fechas, sin días y sin horario,
            la promoción está siempre vigente.
          </p>

          <div className="mt-2 flex flex-wrap gap-3">
            <label className="text-sm text-ink-700">
              Desde
              <input
                type="date"
                value={values.dateFrom}
                onChange={(e) => set("dateFrom", e.target.value)}
                className="ml-2 rounded-md border border-ink-200 px-2 py-1.5 text-sm"
              />
            </label>
            <label className="text-sm text-ink-700">
              Hasta
              <input
                type="date"
                value={values.dateTo}
                onChange={(e) => set("dateTo", e.target.value)}
                className="ml-2 rounded-md border border-ink-200 px-2 py-1.5 text-sm"
              />
            </label>
          </div>
          {err("dateTo")}
          {err("dateFrom")}

          <div className="mt-3 flex flex-wrap gap-1.5">
            {WEEKDAY_LABELS.map((label, day) => (
              <button
                key={label}
                type="button"
                aria-pressed={values.weekdays.includes(day)}
                onClick={() => toggleWeekday(day)}
                className={`rounded-md border px-2.5 py-1 text-xs ${
                  values.weekdays.includes(day)
                    ? "border-ink-900 bg-ink-900 text-white"
                    : "border-ink-200 text-ink-700"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <p className="mt-1 text-xs text-ink-500">
            Sin días seleccionados aplica todos los días.
          </p>

          <div className="mt-3 flex flex-wrap gap-3">
            <label className="text-sm text-ink-700">
              De
              <input
                type="time"
                value={values.startTime}
                onChange={(e) => set("startTime", e.target.value)}
                className="ml-2 rounded-md border border-ink-200 px-2 py-1.5 text-sm"
              />
            </label>
            <label className="text-sm text-ink-700">
              a
              <input
                type="time"
                value={values.endTime}
                onChange={(e) => set("endTime", e.target.value)}
                className="ml-2 rounded-md border border-ink-200 px-2 py-1.5 text-sm"
              />
            </label>
          </div>
          {err("endMinute")}
          <p className="mt-1 text-xs text-ink-500">
            El horario se evalúa en la zona horaria del negocio.
          </p>
        </fieldset>

        {/* ── Visibility: the decision this feature exists for ── */}
        <fieldset className="mt-5">
          <legend className="text-sm font-medium text-ink-800">
            Qué le dice el agente al cliente
          </legend>
          <label className="mt-2 flex items-start gap-2 text-sm text-ink-700">
            <input
              type="radio"
              name="promotion-announce"
              className="mt-1"
              checked={values.announce}
              onChange={() => set("announce", true)}
            />
            <span>
              <strong className="font-medium">Menciona la promoción.</strong> El agente
              puede decir que el precio es promocional y cómo se llama.
            </span>
          </label>
          <label className="mt-2 flex items-start gap-2 text-sm text-ink-700">
            <input
              type="radio"
              name="promotion-announce"
              className="mt-1"
              checked={!values.announce}
              onChange={() => set("announce", false)}
            />
            <span>
              <strong className="font-medium">Sólo el precio nuevo.</strong> El agente
              cotiza el precio sin mencionar que hay una promoción. Úsalo cuando el precio
              cambie según la hora o el día.
            </span>
          </label>
        </fieldset>

        {/* ── Preview: the screen before a real customer hears the number ── */}
        {chosen.length > 0 ? (
          <div className="mt-5 rounded-md border border-ink-200 bg-ink-50 p-3">
            <p className="text-xs font-medium uppercase tracking-wide text-ink-500">
              Cómo quedarían los precios
            </p>
            <ul className="mt-2 space-y-1 text-sm">
              {chosen.map((p) => {
                const preview = previewPriceCents(p.priceCents, values);
                return (
                  <li key={p.id} className="flex items-center justify-between gap-3">
                    <span className="text-ink-800">{p.name}</span>
                    <span className="text-ink-700">
                      {p.priceCents === null ? (
                        <span className="text-ink-500">Sin precio configurado</span>
                      ) : preview === null ? (
                        <span className="text-ink-500">—</span>
                      ) : (
                        <>
                          <span className="text-ink-400 line-through">
                            {formatMxn(p.priceCents, currency)}
                          </span>{" "}
                          <strong className="font-medium">
                            {formatMxn(preview, currency)}
                          </strong>
                        </>
                      )}
                    </span>
                  </li>
                );
              })}
            </ul>
            {/* Mirrors the agent's own downgrade rule, so the editor never
                promises an announcement the agent will refuse to make. */}
            {values.announce && chosen.some((p) => !willAnnounce(values, p.priceCents)) ? (
              <p className="mt-2 text-xs text-amber-700">
                En los productos donde el precio no baja, el agente no la presentará como
                promoción: sólo dirá el precio.
              </p>
            ) : null}
          </div>
        ) : null}

        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-md border border-ink-200 px-3 py-2 text-sm text-ink-700"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={saving}
            className="rounded-md bg-ink-900 px-3 py-2 text-sm text-white disabled:opacity-50"
          >
            {saving ? "Guardando…" : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  );
}
